import React from 'react'
import { KnowFormat } from 'interfaces/Know'
import { GetServerSideProps } from 'next'
import { getPathFromUrl, getPathWithOldQueryParams, parseUuidFromSlug } from 'lib/formatting/url'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { parseLocale } from 'lib/constants/localization'
import { getDomainByCountryCode } from 'lib/constants/country'
import KnowSeoSection from 'components/feature/Know/view/SEO/KnowSEOSection'
import { knowViewRoute, unindexedKnowRoute } from 'lib/constants/routes'
import {
    fetchKnowFlashcardsSSR,
    fetchKnowSEOInfoSSR,
    shouldKnowBeRedirectionOccurBasedOnLanguage,
} from 'lib/features/Know/knowSSR'
import { Flashcard } from 'interfaces/Flashcard'
import KnowPageSessionDurationTracking from 'components/modules/Knows/KnowPageSessionDurationTracking'
import { KnowSeoInfo } from 'interfaces/KnowSeoInfo'
import { getCountryConfig } from 'lib/constants/countryconfig'
import { isMobile } from 'lib/tracking/attribution/device'
import { captureSentryException } from 'lib/formatting/sentry'
import { ActiveExperiments, getActiveExperiments } from 'lib/features/activeExperiments'

interface Props {
    flashcards: Flashcard[] | null
    knowSeoInfo: KnowSeoInfo
    unblurContentForSmallerMarkets: boolean
    isMobileDevice: boolean | null
    experiments: ActiveExperiments
}

function Know(props: Props) {
    return (
        <>
            <KnowPageSessionDurationTracking know={props.knowSeoInfo.know} />
            <KnowSeoSection
                {...props}
                similarKnows={props.knowSeoInfo.similarKnows}
                know={props.knowSeoInfo.know}
                transcript={{ transcript: props.knowSeoInfo.transcript }}
            />
        </>
    )
}

export const getServerSideProps: GetServerSideProps<Props> = async (context) => {
    context.res.setHeader('Cache-Control', 'public, s-maxage=86400, stale-while-revalidate=59')

    if (!context.params || typeof context.params.slug !== 'string') {
        return { notFound: true }
    }

    const knowIdentifier = parseUuidFromSlug(context.params.slug) as string
    if (knowIdentifier === null) {
        return { notFound: true }
    }

    const userAgent = context.req.headers['user-agent']
    const isMobileDevice = userAgent ? Boolean(isMobile(userAgent)) : null

    const parsedLocale = parseLocale(context.locale)
    const knowSeoInfo = await fetchKnowSEOInfoSSR(knowIdentifier)
    const { unblurContentForSmallerMarkets } = getCountryConfig(parsedLocale.countryCode)

    if (knowSeoInfo === null) {
        captureSentryException('Know is not found')
        return { notFound: true }
    }

    if (knowSeoInfo === undefined) {
        captureSentryException('Know is forbidden')
        return {
            redirect: {
                permanent: false,
                destination: knowViewRoute(knowIdentifier),
            },
        }
    }

    const { know } = knowSeoInfo

    const route = unindexedKnowRoute(know.slug)

    if (route !== know.websitePath) {
        return {
            redirect: {
                permanent: true,
                destination: know.websitePath,
            },
        }
    }

    if (know.knowFormat === KnowFormat.PracticeSet) {
        return { notFound: true }
    }

    const domain = getDomainByCountryCode(know.knowerCountry.code)

    if (shouldKnowBeRedirectionOccurBasedOnLanguage(know, parsedLocale)) {
        return {
            redirect: {
                statusCode: 301,
                destination: `https://${domain}${know.websitePath}`,
            },
        }
    }

    if (getPathFromUrl(context.resolvedUrl) !== unindexedKnowRoute(know.slug)) {
        return {
            redirect: {
                permanent: true,
                destination: getPathWithOldQueryParams(unindexedKnowRoute(know.slug), context.resolvedUrl),
            },
        }
    }

    let flashcards: Flashcard[] | null = null

    if (know.knowFormat === KnowFormat.Flashcards) {
        flashcards = await fetchKnowFlashcardsSSR(know.uuid)
    }

    const translations = await serverSideTranslations(parsedLocale.interfaceLanguageCode, [
        'common',
        'knows',
        'auth',
        'app',
        'homepage',
    ])
    const experiments = getActiveExperiments(context)

    return {
        props: {
            know,
            knowSeoInfo,
            flashcards,
            unblurContentForSmallerMarkets: !!unblurContentForSmallerMarkets,
            ...translations,
            isMobileDevice,
            experiments,
        },
    }
}

export default Know
